import {
  DATA_CHANNEL_NAMES,
  MEDIA_LIMITS,
  PROTOCOL_VERSION,
  evaluateSdpPolicy,
  parseChannelMessage,
  parseSignallingEnvelope,
  peerConnectionConfig,
  planClipboardWire,
  resolveIceServers,
  reviewClipboardSet,
  signalrTarget,
  type ClipboardSet,
  type DataChannelName,
  type Display,
  type IceServer,
  type ParseResult,
  type SignallingEnvelope,
} from "wylo-rc-session";
import type { Session } from "../api/parse.ts";
import { mediaGrant } from "./model.ts";

export { DATA_CHANNEL_NAMES, type DataChannelName };

const encoder = new TextEncoder();

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const step = 0x8000;
  for (let index = 0; index < bytes.length; index += step) {
    const slice = bytes.subarray(index, index + step);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

export function base64ToBytes(payload: string): Uint8Array | undefined {
  try {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  } catch {
    return undefined;
  }
}

export function requireSignalTarget(session: Session): { hub: "rc"; method: "signal"; group: string } {
  const target = signalrTarget(session.sessionId);
  if (session.signalr.hub !== target.hub || session.signalr.method !== target.method || session.signalr.group !== target.group) {
    throw new Error("session signalling target does not match wylo-rc-session");
  }
  return target;
}

export function iceConfigFor(session: Session): { ok: true; config: RTCConfiguration } | { ok: false; reason: string } {
  const iceServers: IceServer[] = session.iceServers.map((server) => {
    const copy: IceServer = { urls: [...server.urls] };
    if (server.username !== undefined) copy.username = server.username;
    if (server.credential !== undefined) copy.credential = server.credential;
    return copy;
  });
  const resolved = resolveIceServers({ turnEnabled: session.turnEnabled, iceServers });
  if (!resolved.ok) return resolved;
  const config = peerConnectionConfig(resolved);
  return { ok: true, config };
}

export function assertCanOpen(session: Session): void {
  if (!mediaGrant(session)) throw new Error("refusing to open media without consent");
  requireSignalTarget(session);
  const ice = iceConfigFor(session);
  if (!ice.ok) throw new Error(ice.reason);
}

export function buildSelectDisplay(sessionId: string, displayId: string) {
  return parseSignallingEnvelope({
    v: PROTOCOL_VERSION,
    sessionId,
    type: "select-display",
    payload: { displayId },
  });
}

function rejected(errors: string[]): { ok: false; errors: string[] } {
  return { ok: false, errors };
}

export function buildOffer(sessionId: string, sdp: string) {
  const policy = evaluateSdpPolicy(sdp);
  if (!policy.ok) return rejected(policy.violations.map((violation) => violation.code));
  return parseSignallingEnvelope({
    v: PROTOCOL_VERSION,
    sessionId,
    type: "sdp-offer",
    payload: { type: "offer", sdp },
  });
}

export function buildIceCandidate(sessionId: string, candidate: RTCIceCandidate | null): ParseResult<SignallingEnvelope> {
  return parseSignallingEnvelope({
    v: PROTOCOL_VERSION,
    sessionId,
    type: "ice-candidate",
    payload: {
      candidate: candidate?.candidate ?? "",
      sdpMid: candidate?.sdpMid ?? null,
      sdpMLineIndex: candidate?.sdpMLineIndex ?? null,
    },
  });
}

export function buildHello(session: Session, id: string, ts: string) {
  return parseChannelMessage("control", {
    v: PROTOCOL_VERSION,
    type: "control.hello",
    id,
    ts,
    features: {
      clipboard: session.features.clipboard,
      fileTransfer: session.features.fileTransfer,
    },
  });
}

export type ClipboardBuild =
  | { ok: true; message: Extract<ReturnType<typeof parseChannelMessage<"clipboard">>, { ok: true }>["value"] }
  | { ok: false; reason: "disabled" | "limit" | "size-mismatch" | "bad-payload" | "invalid" };

export type ClipboardPlan =
  | { ok: true; messages: ClipboardSet[] }
  | { ok: false; reason: "disabled" | "limit" | "size-mismatch" | "bad-payload" | "invalid" };

export function planClipboardBytes(input: {
  enabled: boolean;
  bytes: Uint8Array;
  mime: "text/plain" | "image/png";
  direction: "ToDevice" | "ToOperator";
  id: string;
  ts: string;
}): ClipboardPlan {
  if (!input.enabled) return { ok: false, reason: "disabled" };
  if (input.mime === "image/png" && !isPng(input.bytes)) return { ok: false, reason: "bad-payload" };
  if (input.bytes.byteLength > MEDIA_LIMITS.maxClipboardBytes) return { ok: false, reason: "limit" };
  let messages: ClipboardSet[];
  try {
    messages = planClipboardWire(input);
  } catch {
    return { ok: false, reason: "invalid" };
  }
  for (const message of messages) {
    const parsed = parseChannelMessage("clipboard", message);
    if (!parsed.ok) return { ok: false, reason: "invalid" };
  }
  return { ok: true, messages };
}

const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export function isPng(bytes: Uint8Array): boolean {
  if (bytes.byteLength < pngSignature.length) return false;
  for (let index = 0; index < pngSignature.length; index += 1) {
    if (bytes[index] !== pngSignature[index]) return false;
  }
  return true;
}

export function buildClipboardPng(input: {
  enabled: boolean;
  bytes: Uint8Array;
  direction: "ToDevice" | "ToOperator";
  id: string;
  ts: string;
}): ClipboardBuild {
  if (!isPng(input.bytes)) return { ok: false, reason: "bad-payload" };
  if (input.bytes.byteLength > MEDIA_LIMITS.maxClipboardBytes) return { ok: false, reason: "limit" };
  const message = {
    v: PROTOCOL_VERSION,
    type: "clipboard.set" as const,
    id: input.id,
    ts: input.ts,
    direction: input.direction,
    mime: "image/png" as const,
    byteLength: input.bytes.byteLength,
    payloadBase64: bytesToBase64(input.bytes),
  };
  const review = reviewClipboardSet({ message, clipboardEnabled: input.enabled });
  if (!review.ok) return review;
  const parsed = parseChannelMessage("clipboard", message);
  if (!parsed.ok) return { ok: false, reason: "invalid" };
  return { ok: true, message: parsed.value };
}

export function buildClipboard(input: {
  enabled: boolean;
  text: string;
  direction: "ToDevice" | "ToOperator";
  id: string;
  ts: string;
}): ClipboardBuild {
  const bytes = encoder.encode(input.text);
  if (bytes.byteLength > MEDIA_LIMITS.maxClipboardBytes) return { ok: false, reason: "limit" };
  const message = {
    v: PROTOCOL_VERSION,
    type: "clipboard.set" as const,
    id: input.id,
    ts: input.ts,
    direction: input.direction,
    mime: "text/plain" as const,
    byteLength: bytes.byteLength,
    payloadBase64: bytesToBase64(bytes),
  };
  const review = reviewClipboardSet({ message, clipboardEnabled: input.enabled });
  if (!review.ok) return review;
  const parsed = parseChannelMessage("clipboard", message);
  if (!parsed.ok) return { ok: false, reason: "invalid" };
  return { ok: true, message: parsed.value };
}

export function unitInterval(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

export function buildPointer(input: {
  displayId: string;
  x: number;
  y: number;
  buttons: number;
  action: "move" | "down" | "up";
  id: string;
  ts: string;
}) {
  return parseChannelMessage("control", {
    v: PROTOCOL_VERSION,
    type: "input.pointer",
    id: input.id,
    ts: input.ts,
    displayId: input.displayId,
    x: unitInterval(input.x),
    y: unitInterval(input.y),
    buttons: input.buttons,
    action: input.action,
  });
}

export function buildKey(input: {
  action: "down" | "up";
  code: string;
  modifiers: Array<"Control" | "Alt" | "Shift" | "Meta">;
  id: string;
  ts: string;
}) {
  return parseChannelMessage("control", {
    v: PROTOCOL_VERSION,
    type: "input.key",
    id: input.id,
    ts: input.ts,
    action: input.action,
    code: input.code,
    modifiers: input.modifiers,
  });
}

export function buildCad(input: { sessionId: string; requestId: string; ts: string }) {
  return parseChannelMessage("control", {
    type: "input.cad",
    sessionId: input.sessionId,
    requestId: input.requestId,
    ts: input.ts,
  });
}

export function buildWheel(input: { displayId: string; deltaX: number; deltaY: number; id: string; ts: string }) {
  return parseChannelMessage("control", {
    v: PROTOCOL_VERSION,
    type: "input.wheel",
    id: input.id,
    ts: input.ts,
    displayId: input.displayId,
    deltaX: input.deltaX,
    deltaY: input.deltaY,
  });
}

export function chooseDisplay(displays: readonly Display[]): Display | undefined {
  if (displays.length === 0) return undefined;
  return displays.find((display) => display.primary) ?? displays[0];
}

export function decodeClipboardText(payloadBase64: string): string | undefined {
  const bytes = base64ToBytes(payloadBase64);
  if (!bytes) return undefined;
  return new TextDecoder().decode(bytes);
}
